@use "../../global/styles" as *;
@import url("https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap");

/* Import icon font */
@font-face {
  font-family: "icon-font";
  src: url("../../../assets/fonts/icon-font/icon-font.woff") format("woff");
}

.button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-family: var(--font-dialect-light);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4;
  border-radius: var(--border-radius-pill);
  border: none;
  cursor: pointer;
  position: relative;
  text-align: center;
  transition: var(--transition-normal);
  min-width: 7.875rem;
  max-height: 3rem;
  padding: 1.15rem 1.5rem;
  box-sizing: content-box;

  p {
    margin: 0;
  }

  .button-content {
    display: inline-flex;
    align-items: center;
  }

  @media (max-width: 768px) {
    min-width: 6.875rem;
    max-height: 2.5rem;
    padding: 0.8125rem 1rem;
  }

  @media (max-width: 480px) {
    width: 100%;
    box-sizing: border-box;
  }
}

button,
a {
  &.button.primary.active {
    background-color: var(--color-btn-primary);
    color: var(--color-btn-text-primary);
    text-decoration: none;

    &:hover {
      background-color: var(--color-btn-primary-hover);
    }
  }

  &.button.primary:focus {
    border: 1px dashed var(--color-btn-primary-focus-border);
  }

  &.button.primary.inactive {
    background-color: var(--color-btn-primary-disabled);
    color: var(--color-text-inverse);
    border: 1px solid var(--color-btn-primary-disabled);
    cursor: not-allowed;
  }

  &.button.secondary.active {
    background-color: transparent;
    color: var(--color-btn-text-secondary);
    border: 1px solid var(--color-btn-text-secondary);
    text-decoration: none;

    &:hover {
      background-color: var(--color-btn-secondary-hover);
      border: 1px solid var(--color-btn-secondary);
      color: var(--color-btn-text-hover-secondary);
    }
  }

  &.button.secondary:focus {
    border: 1px dashed var(--color-btn-secondary-focus-border);
  }

  &.button.secondary.inactive {
    color: var(--color-btn-secondary-disabled);
    border: 1px solid var(--color-btn-secondary-disabled);
    cursor: not-allowed;
  }
}

/* Icon Button Styles */
.icon-button {
  font-family: var(--font-dialect-light);
  border-radius: 50%;
  width: 2.5rem; // 40px
  height: 2.5rem; // 40px
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  position: relative;
  transition: var(--transition-normal);
}

.icon-button.s {
  width: 2rem; // 32px
  height: 2rem; // 32px
}

.icon-button.primary.active {
  background-color: var(--color-btn-primary);
  color: var(--color-btn-text-primary);
  text-decoration: none;
  &:hover {
    background-color: var(--color-btn-primary-hover);
  }
}

.icon-button.secondary.active,
.icon-button.tertiary.active {
  background-color: transparent;
  color: var(--color-btn-text-secondary);
}

.icon-button.secondary.active {
  border: 1px solid var(--color-btn-secondary);
}

.icon-button.secondary.hover,
.icon-button.tertiary.hover {
  background-color: var(--color-btn-secondary-hover);
  color: var(--color-btn-secondary);
}

/* Link Button Styles */
.link-button {
  font-family: var(--font-dialect-light);
  font-size: 0.875rem; // 14px
  font-weight: 400;
  text-decoration: none;
  position: relative;
  display: inline-block;
  text-decoration: none;
  transition: var(--transition-normal);
}

.link-button .container {
  padding: 0.5rem 0; // 8px 0
  border-bottom: 1px solid;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.link-button .button-content {
  display: inline-flex;
  align-items: center;
}

a.link-button.active {
  color: var(--color-text-secondary);
  text-decoration: none;
}

a.link-button.hover {
  color: var(--color-primary);
  text-decoration: none;
}

.link-button.inactive {
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

/* Text Link Styles */
.text-link {
  font-family: var(--font-dialect-light);
  font-size: 0.875rem; // 14px
  font-weight: 400;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  position: relative;
  transition: var(--transition-normal);
}

.text-link.L {
  font-size: 1.25rem; // 20px
  font-weight: 500;
}

.text-link .arrow-icon {
  margin-left: 0.5rem; // 8px
}

.text-link.active {
  color: var(--color-text-link);
}

.text-link.hover {
  color: var(--color-text-link-hover);
}

.text-link.inactive {
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

/* Carousel Button Styles */
.carousel-button {
  width: 2.5rem; // 40px
  height: 2.5rem; // 40px
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  position: relative;
  background-color: var(--color-bg-overlay);
  color: var(--color-text-secondary);
  transition: var(--transition-normal);
}

.carousel-button.hover {
  background-color: var(--color-bg-secondary);
}

/* Media Button Styles */
.media-button {
  width: 3rem; // 48px
  height: 3rem; // 48px
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  position: relative;
  background-color: var(--color-bg-primary);
  color: var(--color-text-secondary);
  transition: var(--transition-normal);
}

/* Focus Ring */
.focus-ring {
  position: absolute;
  top: -0.25rem; // -4px
  left: -0.25rem; // -4px
  right: -0.25rem; // -4px
  bottom: -0.25rem; // -4px
  border-radius: inherit;
  border: 0.125rem solid var(--color-border-focus); // 2px
  pointer-events: none;
}

.focused {
  outline: none;
}

/* Button Icon Styles */
a.button[target="_self"],
a.button.vsr-button-same-window,
a.button[target="modal"],
a.button.vsr-button-modal,
a.button[target="_blank"],
a.button.vsr-button-new-window,
a.button.vsr-button-external,
a.button.vsr-button-video {
  position: relative;
}

/* Link Button Icon Styles - target the container */
a.link-button.vsr-button-same-window .container,
a.link-button.vsr-button-modal .container,
a.link-button.vsr-button-new-window .container,
a.link-button.vsr-button-external .container,
a.link-button.vsr-button-video .container {
  position: relative;
}

/* Shared base style for all button icons */
a.button[target="_self"]::after,
a.button.vsr-button-same-window::after,
a.button[target="modal"]::after,
a.button.vsr-button-modal::after,
a.button[target="_blank"]::after,
a.button.vsr-button-new-window::after,
a.button.vsr-button-external::after,
a.button.vsr-button-video::after {
  font-style: normal;
  font-weight: normal !important;
  font-variant: normal;
  font-size: 16px;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-family: "icon-font" !important;
  margin-left: 8px;
  display: inline-block;
  vertical-align: text-bottom;
}

/* Link button icons - separate styling for better alignment */
a.link-button.vsr-button-same-window .container::after,
a.link-button.vsr-button-modal .container::after,
a.link-button.vsr-button-new-window .container::after,
a.link-button.vsr-button-external .container::after,
a.link-button.vsr-button-video .container::after {
  font-style: normal;
  font-weight: normal !important;
  font-variant: normal;
  font-size: 16px;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-family: "icon-font" !important;
  margin-left: 8px;
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

/* Same window icon */
a.button[target="_self"]::after,
a.button.vsr-button-same-window::after,
a.link-button.vsr-button-same-window .container::after {
  content: "\ea17" !important;
}

/* Modal icon */
a.button[target="modal"]::after,
a.button.vsr-button-modal::after,
a.link-button.vsr-button-modal .container::after {
  content: "\eab2" !important;
}

/* New window icon */
a.button[target="_blank"]::after,
a.button.vsr-button-new-window::after,
a.link-button.vsr-button-new-window .container::after {
  content: "\ea92" !important;
}

/* Video icon */
a.button.vsr-button-video::after,
a.link-button.vsr-button-video .container::after {
  content: "\eac7" !important;
}

/* External icon */
a.button.vsr-button-external::after,
a.link-button.vsr-button-external .container::after {
  content: "\ea1c" !important;
}
